import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import StatusIndicator from './StatusIndicator';

/**
 * KPI Card (§7.2)
 *
 * Props:
 *   title:        string
 *   value:        number | null
 *   unit:         string  (e.g. 'kWh/kL', '₹/kL')
 *   trend:        number | null  — signed % change vs previous equivalent period
 *   trendIsGood:  'up' | 'down'  — which direction is "good" for this metric.
 *                 'down' for intensity / consumption metrics (lower is better);
 *                 'up' for production / output metrics (higher is better).
 *                 Defaults to 'down'. Drives the trend badge color (green when
 *                 the change matches the good direction, amber when opposite).
 *   trendLabel:   string — tooltip suffix, e.g. "vs previous 24h"
 *   status:       'PROVISIONAL' | 'FINAL' | 'MIXED' | 'EMPTY'
 *   subtle:       optional secondary line under the value
 */
const fmtValue = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 10)   return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const TrendBadge = ({ trend, trendIsGood = 'down', trendLabel }) => {
  // No comparison data — previous window has no rows / sums to zero.
  // Distinct from "flat" so users can tell why no arrow shows.
  if (trend == null || Number.isNaN(trend)) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] text-gray-400 italic"
        title={`No data ${trendLabel || 'in previous period'} — trend not available. Run Compute over a longer range to populate it.`}
      >
        <span className="text-gray-300">—</span>
        no prev data
      </span>
    );
  }
  // Treat very small changes as flat — avoids noisy "+0.04%" badges
  const FLAT_THRESHOLD = 0.5; // percent
  if (Math.abs(trend) < FLAT_THRESHOLD) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-gray-500"
        title={`Change ${trendLabel || 'vs previous period'}: <${FLAT_THRESHOLD}% (effectively unchanged)`}
      >
        <Minus className="w-3 h-3" />
        flat
      </span>
    );
  }
  const up = trend > 0;
  const direction = up ? 'up' : 'down';
  const isGood = direction === trendIsGood;
  const colorClass = isGood ? 'text-green-700' : 'text-amber-700';
  const sign = up ? '+' : '−';
  const tooltip = `${sign}${Math.abs(trend).toFixed(2)}% ${trendLabel || 'vs previous period'} (${isGood ? 'better' : 'worse'})`;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${colorClass}`}
      title={tooltip}
    >
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {sign}{Math.abs(trend).toFixed(1)}%
    </span>
  );
};

const KpiCard = ({
  title,
  value,
  unit,
  trend,
  trendIsGood = 'down',
  trendLabel,
  status,
  subtle,
  accent = 'blue',
}) => {
  const accentBorder = {
    blue:    'border-blue-200',
    indigo:  'border-indigo-200',
    teal:    'border-teal-200',
    amber:   'border-amber-200',
  }[accent] || 'border-blue-200';

  return (
    <div className={`bg-white rounded-lg border-2 ${accentBorder} p-5 shadow-sm`}>
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          {title}
        </h3>
        {status && <StatusIndicator status={status} small />}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900">{fmtValue(value)}</span>
        {unit && <span className="text-sm font-medium text-gray-500">{unit}</span>}
      </div>

      <div className="mt-3 flex items-center justify-between">
        {subtle ? (
          <span className="text-xs text-gray-500">{subtle}</span>
        ) : <span />}
        <TrendBadge trend={trend} trendIsGood={trendIsGood} trendLabel={trendLabel} />
      </div>
    </div>
  );
};

export default KpiCard;
